import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Paper, TextField, InputAdornment, Chip,
  Stack, Button, Typography, Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  IconButton, Menu, MenuItem, FormControl, Select, CircularProgress,
  Card, CardContent, Divider, CssBaseline, alpha,
  Tabs, Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Route as RouteIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  PersonAdd as PersonAddIcon,
  Assessment as AssessmentIcon,
  Map as MapIcon
} from '@mui/icons-material';
import AppNavbar from '../../../components/AppNavbar';
import Header from '../../../components/Header';
import SideMenu from '../../../components/SideMenu';
import AppTheme from '../../../shared-theme/AppTheme';
import { StyledCellHeader } from '../../../components/StyledCellHeader';
import CrearRutaDialog from './rutas/crearrutadialog';
import AsignarPrestamosDialog from './rutas/asignarprestamos';
import AsignarCobradorDialog from './rutas/asignarcobrador';
import CrearCobradorDialog from './cobradores/crearcobradoradialog';
import AlertMessageDialog from '../../../components/Dialogs/alertMessageDialog';
import OnGeneralMessageDialog from '../../../components/Dialogs/OnGeneralMessageDialog';
import AccessDeniedBox from '../../../components/AccessDeniedBox';
import { useNavigate } from 'react-router-dom';
import { formatNumberWithCommas } from '../../../utils/generalFunctions';

const RUTA_STATUSES = [
  { id: "activa", name: "Activa", color: "success" },
  { id: "inactiva", name: "Inactiva", color: "default" }
];

const COBRADOR_STATUSES = [
  { id: "activo", name: "Activo", color: "success" },
  { id: "inactivo", name: "Inactivo", color: "default" },
  { id: "suspendido", name: "Suspendido", color: "error" }
];

function EmptyState({ icon: Icon, title, description, actionLabel, onAction }) {
  return (
    <Box sx={{ textAlign: 'center', py: 9, px: 2 }}>
      <Box
        sx={{
          width: 72, height: 72, borderRadius: '50%',
          bgcolor: 'action.hover',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mx: 'auto', mb: 2.5,
        }}
      >
        <Icon sx={{ fontSize: 34, color: 'text.disabled' }} />
      </Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 340, mx: 'auto' }}>
        {description}
      </Typography>
      {actionLabel && (
        <Button variant="contained" startIcon={<AddIcon />} onClick={onAction} color="primarybutton">
          {actionLabel}
        </Button>
      )}
    </Box>
  );
}

export default function CobrosMovilesManagement() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  // Rutas state
  const [rutas, setRutas] = useState([]);
  const [rutasLoading, setRutasLoading] = useState(false);
  const [rutasSearchTerm, setRutasSearchTerm] = useState("");
  const [rutasStatusFilter, setRutasStatusFilter] = useState("todos");

  // Cobradores state
  const [cobradores, setCobradores] = useState([]);
  const [cobradoresLoading, setCobradoresLoading] = useState(false);
  const [cobradoresSearchTerm, setCobradoresSearchTerm] = useState("");
  const [cobradoresStatusFilter, setCobradoresStatusFilter] = useState("todos");

  // Reportes state
  const [reportesData, setReportesData] = useState([]);
  const [reportesLoading, setReportesLoading] = useState(false);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  // Common state
  const [loading, setLoading] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openAsignarPrestamosDialog, setOpenAsignarPrestamosDialog] = useState(false);
  const [openAsignarCobradorDialog, setOpenAsignarCobradorDialog] = useState(false);
  const [openCreateCobradorDialog, setOpenCreateCobradorDialog] = useState(false);
  const [selectedRuta, setSelectedRuta] = useState(null);
  const [selectedCobrador, setSelectedCobrador] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    fetchRutas();
    fetchCobradores();
    fetchReportes();
  }, []);

  const fetchRutas = async () => {
    setRutasLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/rutas/${localStorage.getItem('idcompany')}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });
      if (response.ok) {
        const data = await response.json();
        setRutas(data);
        setAccessDenied(false);
      } else {
        setAccessDenied(true);
      }
    } catch (error) {
      console.error('Error fetching rutas:', error);
      setAccessDenied(true);
    } finally {
      setRutasLoading(false);
    }
  };

  const fetchCobradores = async () => {
    setCobradoresLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/cobradores/${localStorage.getItem('idcompany')}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCobradores(data);
      }
    } catch (error) {
      console.error('Error fetching cobradores:', error);
    } finally {
      setCobradoresLoading(false);
    }
  };

  const fetchReportes = async () => {
    setReportesLoading(true);
    try {
      const params = new URLSearchParams();
      if (fechaInicio) params.append('fecha_inicio', fechaInicio);
      if (fechaFin) params.append('fecha_fin', fechaFin);
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/rutas/${localStorage.getItem('idcompany')}?${params}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });
      if (response.ok) {
        const data = await response.json();
        setReportesData(data);
      }
    } catch (error) {
      console.error('Error fetching reportes:', error);
    } finally {
      setReportesLoading(false);
    }
  };

  const handleCreateSuccess = (newRuta) => {
    setRutas(prev => [newRuta, ...prev]);
    setAlertTitle("Éxito");
    setAlertMessage("Ruta creada correctamente");
    setAlertSeverity("success");
    setAlertOpen(true);
  };

  const handleUpdateSuccess = (updatedRuta) => {
    setRutas(prev => prev.map(r => r.id === updatedRuta.id ? updatedRuta : r));
    setAlertTitle("Éxito");
    setAlertMessage("Ruta actualizada correctamente");
    setAlertSeverity("success");
    setAlertOpen(true);
  };

  const handleDeleteSuccess = (deletedId) => {
    setRutas(prev => prev.filter(r => r.id !== deletedId));
    setAlertTitle("Éxito");
    setAlertMessage("Ruta desactivada correctamente");
    setAlertSeverity("success");
    setAlertOpen(true);
  };

  const handleViewRuta = (ruta) => {
    navigate(`/gestion_comercial/cobros_moviles/rutas/${ruta.id}`);
  };

  const handleEditRuta = (ruta) => {
    setSelectedRuta(ruta);
    setOpenCreateDialog(true);
  };

  const handleAsignarPrestamos = (ruta) => {
    setSelectedRuta(ruta);
    setOpenAsignarPrestamosDialog(true);
  };

  const handleAsignarCobrador = (ruta) => {
    setSelectedRuta(ruta);
    setOpenAsignarCobradorDialog(true);
  };

  const handleEditCobrador = (cobrador) => {
    setSelectedCobrador(cobrador);
    setOpenCreateCobradorDialog(true);
  };

  const openMenu = (event, item) => {
    setMenuAnchor(event.currentTarget);
    setSelectedRuta(item);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
  };

  const getStatusInfo = (status) => {
    return RUTA_STATUSES.find(s => s.id === status) || RUTA_STATUSES[0];
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-DO', { style: "currency", currency: "DOP" }).format(amount);
  };

  const filteredRutas = rutas.filter(ruta => {
    const matchesSearch =
      ruta.nombre.toLowerCase().includes(rutasSearchTerm.toLowerCase()) ||
      ruta.zona_geografica?.toLowerCase().includes(rutasSearchTerm.toLowerCase()) ||
      ruta.cobrador_nombre?.toLowerCase().includes(rutasSearchTerm.toLowerCase());
    const matchesStatus = rutasStatusFilter === "todos" || ruta.estado === rutasStatusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredCobradores = cobradores.filter(cobrador => {
    const matchesSearch =
      cobrador.nombre.toLowerCase().includes(cobradoresSearchTerm.toLowerCase()) ||
      cobrador.identificacion.includes(cobradoresSearchTerm) ||
      cobrador.telefono?.includes(cobradoresSearchTerm);
    const matchesStatus = cobradoresStatusFilter === "todos" || cobrador.estado === cobradoresStatusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalPrestamos = rutas.reduce((sum, r) => sum + (r.prestamos_activos || 0), 0);
  const totalCobrado = rutas.reduce((sum, r) => sum + (r.total_cobrado || 0), 0);

  if (accessDenied) {
    return <AccessDeniedBox />;
  }

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <Box component="main" sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Stack spacing={2} sx={{ alignItems: 'center', mx: 3, pb: 5, mt: { xs: 8, md: 0 } }}>
            <Header parent={"Gestión Comercial"} child={"Cobros Móviles"} />
            <Box sx={{ width: '100%', maxWidth: 1200, px: { xs: 0, md: 2 }, py: 3 }}>

              {/* Actions */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="contained"
                  color="primarybutton"
                  startIcon={<MapIcon />}
                  onClick={() => navigate('/gestion_comercial/cobros_moviles/mapa')}
                >
                  Ver mapa en vivo
                </Button>
              </Box>

              {/* KPI Cards */}
              <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
                {[
                  { color: '#2196f3', Icon: RouteIcon,      badge: 'Actualizado', badgeColor: undefined, value: rutas.length,              label: 'Total Rutas' },
                  { color: '#4caf50', Icon: PersonIcon,     badge: 'Activos',     badgeColor: 'success', value: cobradores.length,          label: 'Cobradores' },
                  { color: '#ff9800', Icon: AssignmentIcon, badge: 'Pendientes',  badgeColor: 'warning', value: totalPrestamos,             label: 'Préstamos Asignados' },
                  { color: '#9c27b0', Icon: TrendingUpIcon, badge: 'Hoy',        badgeColor: undefined, value: formatCurrency(totalCobrado), label: 'Total Cobrado' },
                ].map(({ color, Icon, badge, badgeColor, value, label }) => (
                  <Card key={label} sx={{ flex: '1 1 0', minWidth: 180, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: alpha(color, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icon sx={{ fontSize: 22, color }} />
                        </Box>
                        <Chip label={badge} size="small" color={badgeColor} sx={{ fontSize: '0.68rem', height: 20 }} />
                      </Box>
                      <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2, mb: 0.5, fontSize: { xs: '1.4rem', sm: '1.8rem' } }}>
                        {value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">{label}</Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              {/* Tabs */}
              <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 3 }}>
                <Tabs
                  value={tabValue}
                  onChange={(e, v) => setTabValue(v)}
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      minHeight: 48,
                      px: 2,
                    },
                    '& .MuiTabs-indicator': { height: 2 },
                  }}
                >
                  <Tab label="Rutas" icon={<RouteIcon fontSize="small" />} iconPosition="start" />
                  <Tab label="Cobradores" icon={<PersonIcon fontSize="small" />} iconPosition="start" />
                  <Tab label="Reportes" icon={<AssessmentIcon fontSize="small" />} iconPosition="start" />
                </Tabs>
              </Box>

              {/* Tab: Rutas */}
              {tabValue === 0 && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="Buscar rutas..."
                      value={rutasSearchTerm}
                      onChange={(e) => setRutasSearchTerm(e.target.value)}
                      sx={{ flex: 1, minWidth: 200, maxWidth: 360 }}
                      slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
                    />
                    <FormControl size="small" sx={{ minWidth: 170 }}>
                      <Select
                        value={rutasStatusFilter}
                        onChange={(e) => setRutasStatusFilter(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="todos">Todos los estados</MenuItem>
                        {RUTA_STATUSES.map((s) => (
                          <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={fetchRutas}
                        disabled={rutasLoading}
                      >
                        Actualizar
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenCreateDialog(true)}
                        color="primarybutton"
                      >
                        Nueva Ruta
                      </Button>
                    </Box>
                  </Box>

                  <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <StyledCellHeader>Ruta</StyledCellHeader>
                          <StyledCellHeader>Zona</StyledCellHeader>
                          <StyledCellHeader>Cobrador Asignado</StyledCellHeader>
                          <StyledCellHeader>Préstamos</StyledCellHeader>
                          <StyledCellHeader>Tasa de Éxito</StyledCellHeader>
                          <StyledCellHeader>Total Cobrado</StyledCellHeader>
                          <StyledCellHeader>Estado</StyledCellHeader>
                          <StyledCellHeader>Acciones</StyledCellHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rutasLoading ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                              <CircularProgress size={32} />
                            </TableCell>
                          </TableRow>
                        ) : filteredRutas.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ border: 0, p: 0 }}>
                              <EmptyState
                                icon={RouteIcon}
                                title="No se encontraron rutas"
                                description="Crea tu primera ruta para comenzar a gestionar cobros móviles"
                                actionLabel="Crear Nueva Ruta Ahora"
                                onAction={() => setOpenCreateDialog(true)}
                              />
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredRutas.map((ruta) => {
                            const statusInfo = getStatusInfo(ruta.estado);
                            return (
                              <TableRow key={ruta.id} hover>
                                <TableCell>
                                  <Typography variant="subtitle2" fontWeight={600}>{ruta.nombre}</Typography>
                                  {ruta.descripcion && (
                                    <Typography variant="caption" color="text.secondary">{ruta.descripcion}</Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">{ruta.zona_geografica || 'No especificada'}</Typography>
                                </TableCell>
                                <TableCell>
                                  {ruta.cobrador_nombre ? (
                                    <Box>
                                      <Typography variant="body2" fontWeight={600}>{ruta.cobrador_nombre}</Typography>
                                      <Typography variant="caption" color="text.secondary">{ruta.cobrador_identificacion}</Typography>
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">Sin asignar</Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={600}>{ruta.prestamos_activos || 0} activos</Typography>
                                  <Typography variant="caption" color="text.secondary">{ruta.prestamos_asignados || 0} total</Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={600}>{ruta.tasa_exito || 0}%</Typography>
                                  <Typography variant="caption" color="text.secondary">{ruta.visitas_exitosas || 0}/{ruta.total_visitas || 0} visitas</Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={600}>{formatCurrency(ruta.total_cobrado || 0)}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip label={statusInfo.name} color={statusInfo.color} size="small" />
                                </TableCell>
                                <TableCell>
                                  <Box display="flex" gap={0.5}>
                                    <IconButton size="small" onClick={() => handleViewRuta(ruta)} sx={{ color: 'primary.main' }}>
                                      <ViewIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={(e) => openMenu(e, ruta)}>
                                      <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Tab: Cobradores */}
              {tabValue === 1 && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="Buscar cobradores..."
                      value={cobradoresSearchTerm}
                      onChange={(e) => setCobradoresSearchTerm(e.target.value)}
                      sx={{ flex: 1, minWidth: 200, maxWidth: 360 }}
                      slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
                    />
                    <FormControl size="small" sx={{ minWidth: 170 }}>
                      <Select
                        value={cobradoresStatusFilter}
                        onChange={(e) => setCobradoresStatusFilter(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="todos">Todos los estados</MenuItem>
                        {COBRADOR_STATUSES.map((s) => (
                          <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={fetchCobradores}
                        disabled={cobradoresLoading}
                      >
                        Actualizar
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenCreateCobradorDialog(true)}
                        color="primarybutton"
                      >
                        Nuevo Cobrador
                      </Button>
                    </Box>
                  </Box>

                  <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <StyledCellHeader>Cobrador</StyledCellHeader>
                          <StyledCellHeader>Contacto</StyledCellHeader>
                          <StyledCellHeader>Estado</StyledCellHeader>
                          <StyledCellHeader>Rutas Asignadas</StyledCellHeader>
                          <StyledCellHeader>Tasa de Éxito</StyledCellHeader>
                          <StyledCellHeader>Total Cobrado</StyledCellHeader>
                          <StyledCellHeader>Acciones</StyledCellHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cobradoresLoading ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                              <CircularProgress size={32} />
                            </TableCell>
                          </TableRow>
                        ) : filteredCobradores.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} sx={{ border: 0, p: 0 }}>
                              <EmptyState
                                icon={PersonIcon}
                                title="No se encontraron cobradores"
                                description="Agrega cobradores para asignarlos a rutas de cobro móvil"
                                actionLabel="Crear Nuevo Cobrador"
                                onAction={() => setOpenCreateCobradorDialog(true)}
                              />
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredCobradores.map((cobrador) => {
                            const statusInfo = COBRADOR_STATUSES.find(s => s.id === cobrador.estado) || COBRADOR_STATUSES[0];
                            return (
                              <TableRow key={cobrador.id} hover>
                                <TableCell>
                                  <Typography variant="subtitle2" fontWeight={600}>{cobrador.nombre}</Typography>
                                  <Typography variant="caption" color="text.secondary">{cobrador.identificacion}</Typography>
                                </TableCell>
                                <TableCell>
                                  {cobrador.telefono && <Typography variant="body2">{cobrador.telefono}</Typography>}
                                  {cobrador.email && (
                                    <Typography variant="caption" color="text.secondary">{cobrador.email}</Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip label={statusInfo.name} color={statusInfo.color} size="small" />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={600}>{cobrador.rutas_activas || 0} activas</Typography>
                                  <Typography variant="caption" color="text.secondary">{cobrador.rutas_asignadas || 0} total</Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={600}>{cobrador.tasa_exito || 0}%</Typography>
                                  <Typography variant="caption" color="text.secondary">{cobrador.visitas_exitosas || 0}/{cobrador.total_visitas || 0} visitas</Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={600}>{formatCurrency(cobrador.total_cobrado || 0)}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Box display="flex" gap={0.5}>
                                    <IconButton size="small" onClick={() => {}} sx={{ color: 'primary.main' }}>
                                      <ViewIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => handleEditCobrador(cobrador)} sx={{ color: 'warning.main' }}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={(e) => openMenu(e, cobrador)}>
                                      <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Tab: Reportes */}
              {tabValue === 2 && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                      size="small"
                      type="date"
                      label="Fecha Inicio"
                      value={fechaInicio}
                      onChange={(e) => setFechaInicio(e.target.value)}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ minWidth: 165 }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="Fecha Fin"
                      value={fechaFin}
                      onChange={(e) => setFechaFin(e.target.value)}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ minWidth: 165 }}
                    />
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={fetchReportes}
                        disabled={reportesLoading}
                      >
                        Actualizar
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AssessmentIcon />}
                        onClick={() => {}}
                      >
                        Exportar Excel
                      </Button>
                    </Box>
                  </Box>

                  <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <StyledCellHeader>Ruta</StyledCellHeader>
                          <StyledCellHeader>Zona</StyledCellHeader>
                          <StyledCellHeader>Cobrador</StyledCellHeader>
                          <StyledCellHeader>Préstamos</StyledCellHeader>
                          <StyledCellHeader>Visitas</StyledCellHeader>
                          <StyledCellHeader>Tasa de Éxito</StyledCellHeader>
                          <StyledCellHeader>Total Cobrado</StyledCellHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportesLoading ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                              <CircularProgress size={32} />
                            </TableCell>
                          </TableRow>
                        ) : reportesData.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} sx={{ border: 0, p: 0 }}>
                              <EmptyState
                                icon={AssessmentIcon}
                                title="No hay datos de reportes"
                                description="Selecciona un rango de fechas y actualiza para ver los reportes de cobro"
                              />
                            </TableCell>
                          </TableRow>
                        ) : (
                          reportesData.map((ruta) => (
                            <TableRow key={ruta.id} hover>
                              <TableCell>
                                <Typography variant="subtitle2" fontWeight={600}>{ruta.nombre}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{ruta.zona_geografica || 'No especificada'}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{ruta.cobrador_nombre || 'Sin asignar'}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>{ruta.prestamos_activos || 0}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>{ruta.total_visitas || 0}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>{ruta.tasa_exito || 0}%</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>{formatCurrency(ruta.total_cobrado || 0)}</Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

            </Box>
          </Stack>
        </Box>
      </Box>

      <CrearRutaDialog
        open={openCreateDialog}
        onClose={() => { setOpenCreateDialog(false); setSelectedRuta(null); }}
        onConfirm={selectedRuta ? handleUpdateSuccess : handleCreateSuccess}
        ruta={selectedRuta}
      />

      <AsignarPrestamosDialog
        open={openAsignarPrestamosDialog}
        onClose={() => { setOpenAsignarPrestamosDialog(false); setSelectedRuta(null); }}
        onConfirm={() => {
          fetchRutas();
          setAlertTitle("Éxito");
          setAlertMessage("Préstamos asignados exitosamente");
          setAlertSeverity("success");
          setAlertOpen(true);
        }}
        rutaId={selectedRuta?.id}
      />

      <AsignarCobradorDialog
        open={openAsignarCobradorDialog}
        onClose={() => { setOpenAsignarCobradorDialog(false); setSelectedRuta(null); }}
        onConfirm={() => {
          fetchRutas();
          setAlertTitle("Éxito");
          setAlertMessage("Cobrador asignado exitosamente");
          setAlertSeverity("success");
          setAlertOpen(true);
        }}
        rutaId={selectedRuta?.id}
      />

      <CrearCobradorDialog
        open={openCreateCobradorDialog}
        onClose={() => { setOpenCreateCobradorDialog(false); setSelectedCobrador(null); }}
        onConfirm={() => {
          fetchCobradores();
          setAlertTitle("Éxito");
          setAlertMessage("Cobrador creado exitosamente");
          setAlertSeverity("success");
          setAlertOpen(true);
        }}
        cobrador={selectedCobrador}
      />

      <AlertMessageDialog
        open={alertOpen}
        setOpen={setAlertOpen}
        message={alertMessage}
        title={alertTitle}
        severity={alertSeverity}
        color={alertSeverity}
        handleClose={() => setAlertOpen(false)}
      />

      <OnGeneralMessageDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={() => {
          if (pendingAction) { pendingAction(); setPendingAction(null); }
          setConfirmDialogOpen(false);
        }}
        title="Confirmar Acción"
        message="¿Estás seguro de que deseas realizar esta acción?"
      />

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={() => { handleViewRuta(selectedRuta); closeMenu(); }}>
          <ViewIcon sx={{ mr: 1 }} /> Ver Detalles
        </MenuItem>
        <MenuItem onClick={() => { handleEditRuta(selectedRuta); closeMenu(); }}>
          <EditIcon sx={{ mr: 1 }} /> Editar
        </MenuItem>
        <MenuItem onClick={() => { handleAsignarPrestamos(selectedRuta); closeMenu(); }}>
          <AssignmentIcon sx={{ mr: 1 }} /> Asignar Préstamos
        </MenuItem>
        <MenuItem onClick={() => { handleAsignarCobrador(selectedRuta); closeMenu(); }}>
          <PersonAddIcon sx={{ mr: 1 }} /> Asignar Cobrador
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setPendingAction(() => () => handleDeleteSuccess(selectedRuta.id));
            setConfirmDialogOpen(true);
            closeMenu();
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} /> Desactivar
        </MenuItem>
      </Menu>
    </AppTheme>
  );
}
